import React, { useState, useEffect } from "react";

import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from "@mui/material";

import HomeIcon from "@mui/icons-material/Home";
import AddIcon from "@mui/icons-material/Add";
import BoltIcon from "@mui/icons-material/Bolt";
import PersonIcon from "@mui/icons-material/Person";

import { useSwipeable } from "react-swipeable";
import { motion, AnimatePresence } from "framer-motion";


import PostPage from "../pages/PostPage";
import QuickMomentWrapper from "../pages/QuickMomentWrapper";

import ProfilePage from "../pages/ProfilePage";
import HomeFeed from "../components/HomeFeed";
import useLiveLocation from "../hooks/useLiveLocation";
import NotificationsPage from "../pages/NotificationsPage";
import QuickMomentPage from "../pages/QuickMomentPage";
import MomentStatusIndicator from "../components/MomentStatusIndicator";






/**
 * 🧭 Swipe order (LEFT → RIGHT)
 */
const VIEWS = ["home", "post", "quick", "profile"];

// 🔴 Bolt icon with live red dot
const BoltWithLiveIndicator = ({ active }) => (
  <Box sx={{ position: "relative", display: "inline-flex" }}>
    <BoltIcon sx={{ fontSize: 26 }} />

    {active && (
      <Box
        sx={{
          position: "absolute",
          top: 2,
          right: 2,
          width: 8,
          height: 8,
          bgcolor: "#EF4444", // red
          borderRadius: "50%",
        }}
      />
    )}
  </Box>
);




export default function MobileLayout() {
  const [view, setView] = useState("home");
  const [direction, setDirection] = useState(0);
  const user = JSON.parse(localStorage.getItem("user"));
  const [showNotifications, setShowNotifications] = useState(false);
  const [hasLiveMoment, setHasLiveMoment] = useState(false);



  useLiveLocation(user?.id);


  useEffect(() => {
  const goHome = () => {
    setDirection(-1);   // 👈 ensures swipe animation
    setView("home");
  };

  const handleGoHome = () => {
    sessionStorage.removeItem("go_home_pending");
    goHome();
  };

  // 🔥 Handle case where event fired BEFORE mount
  if (sessionStorage.getItem("go_home_pending") === "1") {
    sessionStorage.removeItem("go_home_pending");
    goHome();
  }

  window.addEventListener("go-home", handleGoHome);

  return () => {
    window.removeEventListener("go-home", handleGoHome);
  };
}, []);


useEffect(() => {
  const syncMomentState = () => {
    const activeId = sessionStorage.getItem("active_moment_id");
    setHasLiveMoment(!!activeId);
  };

  // Initial load
  syncMomentState();

  // Listen to lifecycle events
  window.addEventListener("moment-accepted", syncMomentState);
  window.addEventListener("moment-ended", syncMomentState);
  window.addEventListener("storage", syncMomentState);

  return () => {
    window.removeEventListener("moment-accepted", syncMomentState);
    window.removeEventListener("moment-ended", syncMomentState);
    window.removeEventListener("storage", syncMomentState);
  };
}, []);

useEffect(() => {
  if (!user?.id) return;

  const interval = setInterval(async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE}/moments/active/${user.id}`
      );
      const data = await res.json();

     if (data.momentId) {
  sessionStorage.setItem("active_moment_id", data.momentId);
  setHasLiveMoment(true);
} else {
  // 🔥 MUST ALWAYS RUN when moment ends
  sessionStorage.removeItem("active_moment_id");
  setHasLiveMoment(false);
}

    } catch (e) {
      console.error("moment poll failed", e);
    }
  }, 3000); // every 3s

  return () => clearInterval(interval);
}, [user?.id]);



useEffect(() => {
  const openNotifications = () => {
    setDirection(1);
    setView("notifications");
  };

  const openPost = () => {
    setDirection(1);
    setView("post");
  };

  window.addEventListener("open-notifications", openNotifications);
  window.addEventListener("open-post", openPost);

  return () => {
    window.removeEventListener("open-notifications", openNotifications);
    window.removeEventListener("open-post", openPost);
  };
}, []);






  /**
   * 👉 Swipe handling
   */
const swipeHandlers = useSwipeable({
  onSwipedLeft: (eventData) => {
    if (eventData.event?.target?.closest?.('[data-scroll-lock="pulse"]')) return;

    setView((prev) => {
      const index = VIEWS.indexOf(prev);
      if (index < VIEWS.length - 1) {
        const next = VIEWS[index + 1];
        setDirection(1);

        // ✅ if user swipes into quick, open moment overlay
        if (next === "quick") {
          window.dispatchEvent(new Event("quick-open"));
        }

        return next;
      }
      return prev;
    });
  },

  onSwipedRight: (eventData) => {
    if (eventData.event?.target?.closest?.('[data-scroll-lock="pulse"]')) return;

    setView((prev) => {
      const index = VIEWS.indexOf(prev);
      if (index > 0) {
        const next = VIEWS[index - 1];
        setDirection(-1);

        // ✅ if user swipes into quick, open moment overlay
        if (next === "quick") {
          window.dispatchEvent(new Event("quick-open"));
        }

        return next;
      }
      return prev;
    });
  },

  trackTouch: true,
  preventScrollOnSwipe: false,
});



  /**
   * 🎞️ Animation variants
   */
  const pageVariants = {
    initial: (dir) => ({
      x: dir > 0 ? "100%" : "-100%",
    }),
    animate: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 280,
        damping: 30,
      },
    },
    exit: (dir) => ({
      x: dir > 0 ? "-100%" : "100%",
      transition: {
        type: "spring",
        stiffness: 280,
        damping: 30,
      },
    }),
  };

  /**
   * 🧠 Render active view
   */
  const renderView = () => {
  switch (view) {
    case "home":
      return <HomeFeed userId={user?.id} />;

    case "post":
      return <PostPage />;

    case "quick":
  return <QuickMomentPage />;



    case "profile":
      return <ProfilePage />;

    case "notifications":
      return <NotificationsPage />;

    default:
      return <HomeFeed userId={user?.id} />;
  }
};


  return (
    <Box
      {...swipeHandlers}
      sx={{
        position: "relative",
        minHeight: "100vh",
        bgcolor: "background.default",
        overflow: "hidden",
      }}
    >

      {/* 🧠 Moment state controller */}
      <MomentStatusIndicator />
    <QuickMomentWrapper />

      {/* 🔄 Animated Pages */}
      <AnimatePresence custom={direction} mode="wait">
        <motion.div
          key={view}
          custom={direction}
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            overflowY: "auto",

            /**
             * ⛔ No bottom padding on Post
             */
            paddingBottom: view === "post" ? 0 : 80,
          }}
        >
          {renderView()}
        </motion.div>
      </AnimatePresence>

      {/* 🔽 Bottom Navigation (HIDDEN ON POST) */}
      {view !== "post" && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <BottomNavigation
            value={view}
                    onChange={(e, newValue) => {
            if (!VIEWS.includes(newValue)) {
              setView(newValue);
              return;
            }

            const currentIndex = VIEWS.indexOf(view);
            const nextIndex = VIEWS.indexOf(newValue);

            setDirection(nextIndex > currentIndex ? 1 : -1);
            setView(newValue);

            if (newValue === "quick") {
              window.dispatchEvent(new Event("quick-open"));
            }

          }}


            showLabels={false}
            sx={{ height: 64 }}
          >
            <BottomNavigationAction value="home" icon={<HomeIcon />} />
            <BottomNavigationAction
              value="post"
              icon={<AddIcon sx={{ fontSize: 30 }} />}
            />
           <BottomNavigationAction
              value="quick"
              icon={<BoltWithLiveIndicator active={hasLiveMoment} />}
              onClick={() => {
                window.dispatchEvent(new Event("quick-open"));
              }}
            />


            <BottomNavigationAction
              value="profile"
              icon={<PersonIcon />}
            />
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}
